// src/components/common/PaymentRecordEntry/PaymentRecordEntry.jsx
//
// One "Payment Details #n" card — amount, payment date, reference, proof (+ cheque details).
// Shared by Create/Edit Offline Sales Order, PaymentRecordsModal drafts and Receive Payment so
// the look and the proof handling (upload / change / remove) stay identical everywhere.
import React, { useState } from 'react';
import { X, DollarSign, Calendar, FileText, Upload, Paperclip } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getImageUrl } from '../../../utils/imageUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import ChequeDetailsFields from '../ChequeDetailsFields/ChequeDetailsFields';
import DatePicker from '../../ui/DatePicker/DatePicker';
import { getLocalTodayYMD, clampToToday } from '../../../utils/dateUtils';
import styles from './PaymentRecordEntry.module.css';

const VALID_PROOF_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB
const isPdfUrl = (url) => !!url && url.toLowerCase().endsWith('.pdf');
const todayYMD = getLocalTodayYMD;

export const PaymentProofField = ({ value, onChange, onUpload, idPrefix, readOnly = false }) => {
  const { t } = useLocalization();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputId = `${idPrefix}-proof`;
  const openPicker = () => document.getElementById(inputId)?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!VALID_PROOF_TYPES.includes(file.type)) {
      setError(t('ecomOrders.invalidProofFormat') || 'Only .jpg, .png or .pdf files are allowed');
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setError(t('ecomOrders.proofTooLarge') || 'File must be under 5MB');
      return;
    }
    setError('');
    setUploading(true);
    try {
      onChange(await onUpload(file));
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed, please try again');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {value ? (
        <div className={styles.proofPreview}>
          {isPdfUrl(value) ? (
            <a href={getImageUrl(value)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
              <Paperclip size={16} />
              {t('ecomOrders.viewProofPdf') || 'View uploaded PDF'}
            </a>
          ) : (
            <a href={getImageUrl(value)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
              <img src={getImageUrl(value)} alt="Payment proof" className={styles.proofThumbnail} />
            </a>
          )}
          {!readOnly && (
            <div className={styles.proofActions}>
              <button type="button" className={styles.changeProofButton} onClick={openPicker} disabled={uploading}>
                {uploading ? (t('common.uploading') || 'Uploading...') : (t('ecomOrders.changeProof') || 'Change')}
              </button>
              <button
                type="button"
                className={`${styles.changeProofButton} ${styles.proofRemoveButton}`}
                onClick={() => { setError(''); onChange(null); }}
                disabled={uploading}
                title={t('common.remove', 'Remove')}
                aria-label={t('common.remove', 'Remove')}
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      ) : !readOnly && (
        <button type="button" className={styles.uploadProofButton} onClick={openPicker} disabled={uploading}>
          <Upload size={14} />
          {uploading ? (t('common.uploading') || 'Uploading...') : (t('ecomOrders.uploadProof') || 'Upload proof')}
        </button>
      )}
      {!readOnly && <input id={inputId} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} style={{ display: 'none' }} />}
      {error && <span className={styles.errorText}>{error}</span>}
    </>
  );
};

const PaymentRecordEntry = ({
  index = 0,
  payment,
  methods = [],
  title,
  onChange,
  onRemove,
  onUploadProof,
  maxAmount,
  errors = {},
  enableCheque = false,
  showChequeErrors = false,
  idPrefix = 'pr',
  locked = false,
  headerBadge = null,
  amountLabel,
  referenceLabel,
}) => {
  const { t } = useLocalization();
  const method = methods.find((m) => m.key === payment.paymentMethodKey);

  return (
    <div className={styles.paymentRecord}>
      <div className={styles.paymentRecordHeader}>
        <span className={styles.recordTitle}>
          <PaymentMethodIcon iconFileName={method?.iconFileName} label={method?.label} />
          {title} #{index + 1}
        </span>
        {headerBadge}
        {!locked && onRemove && (
          <button type="button" className={styles.removeItemBtn} onClick={onRemove}>
            <X size={15} />
          </button>
        )}
      </div>
      <div className={styles.paymentRecordFields}>
        <div className={styles.paymentRecordFieldRow}>
          <div className={styles.formGroup}>
            <label>{amountLabel || t('offlineSales.amount')} *</label>
            <div className={styles.inputWrapper}>
              <DollarSign className={styles.inputIcon} size={16} />
              <input
                type="number"
                min="0"
                step="0.01"
                max={maxAmount}
                placeholder="0.00"
                className={errors.amount ? styles.errorInput : ''}
                value={payment.amount}
                disabled={locked}
                onChange={(e) => onChange('amount', e.target.value)}
              />
            </div>
            {errors.amount && <span className={styles.errorText}>{errors.amount}</span>}
          </div>
          <div className={styles.formGroup}>
            <label><Calendar size={13} /> {t('ecomOrders.paymentDate')}</label>
            <DatePicker
              value={payment.paymentDate || ''}
              onChange={(value) => onChange('paymentDate', clampToToday(value))}
              maxDate={todayYMD()}
              disabled={locked}
              placeholder={t('ecomOrders.paymentDate')}
            />
          </div>
        </div>
        <div className={styles.paymentRecordFieldRow}>
          <div className={styles.formGroup}>
            <label>{referenceLabel || t('offlineSales.reference')}</label>
            <div className={styles.inputWrapper}>
              <FileText className={styles.inputIcon} size={16} />
              <input
                type="text"
                value={payment.reference || ''}
                placeholder={t('ecomOrders.paymentReferencePlaceholder')}
                disabled={locked}
                onChange={(e) => onChange('reference', e.target.value)}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>{t('ecomOrders.paymentProof') || 'Payment Proof'}</label>
            <PaymentProofField
              value={payment.proofUrl}
              onChange={(url) => onChange('proofUrl', url)}
              onUpload={onUploadProof}
              idPrefix={idPrefix}
              readOnly={locked}
            />
          </div>
        </div>
        {enableCheque && !locked && payment.paymentMethodKey === 'cheque' && (
          <ChequeDetailsFields
            idPrefix={`${idPrefix}-cheque`}
            value={payment.cheque}
            onChange={(cheque) => onChange('cheque', cheque)}
            showErrors={showChequeErrors || Boolean(errors.cheque)}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentRecordEntry;
